import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { CapabilityProvider, ConfirmProvider, ToastProvider, notFoundPageFor } from '@region13/ui'
import { parseSearch, stringifySearch } from '@region13/ui/searchFormat'
import { AnalyticsIdentity, initAnalytics } from '@region13/analytics'
import { routeTree } from './routeTree.gen'
import { COACH_NAV } from './lib/nav'
import { queryClient } from './lib/query'
import { api } from './lib/api'
import './index.css'

const router = createRouter({
  routeTree,
  // Unlike admin-spa (mounted at /admin), the coach Worker serves this SPA
  // at the domain root.
  basepath: '/',
  defaultPreload: 'intent',
  scrollRestoration: true,
  // Plain text search values (?week=3,4), not JSON (@region13/ui/searchFormat).
  parseSearch,
  stringifySearch,
  // An address no route matches renders inside the shell (#856).
  defaultNotFoundComponent: notFoundPageFor(COACH_NAV),
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// No key in staging or local development, so this returns false and every
// later capture is inert. See @region13/analytics/src/client.ts.
initAnalytics({ surface: 'coach', key: import.meta.env.VITE_POSTHOG_KEY })

const root = document.getElementById('root')
if (!root) throw new Error('#root not found')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Wired now so any future gated route can use @region13/ui's
          useCan/RequireCapability without touching main.tsx. */}
      <CapabilityProvider meFn={api.me}>
        <AnalyticsIdentity surface="coach" />
        <ToastProvider bottomOffset="5rem">
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </ToastProvider>
      </CapabilityProvider>
    </QueryClientProvider>
  </StrictMode>,
)
